import { Box, CloseButton, Code, Heading, HStack, Text, VStack } from '@chakra-ui/react'
import { useEffect, useState } from 'react'
import type { GraphNode } from './hooks/useKnowledgeGraph'

interface KnowledgeGraphPanelProps {
  node: GraphNode | null
  onClose: () => void
  childNodes: GraphNode[]
}

const TYPE_LABELS: Record<string, string> = {
  enterprise: 'Enterprise',
  site: 'Site',
  area: 'Area',
  line: 'Production Line',
  device: 'Device',
  property: 'Property',
}

export default function KnowledgeGraphPanel({
  node,
  onClose,
  childNodes,
}: KnowledgeGraphPanelProps) {
  const [now, setNow] = useState(Date.now())

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(interval)
  }, [])

  if (!node) {
    return null
  }

  const timeSinceUpdate = node.lastSeen
    ? `${((now - node.lastSeen) / 1000).toFixed(1)}s ago`
    : 'Never'

  return (
    <Box
      position="absolute"
      right={0}
      top={0}
      bottom={0}
      w="320px"
      bg="bg.panel"
      borderLeftWidth="1px"
      borderColor="border"
      p={4}
      overflow="auto"
      zIndex={10}
      boxShadow="lg"
    >
      <HStack justify="space-between" mb={4}>
        <Heading size="md">{node.label}</Heading>
        <CloseButton onClick={onClose} />
      </HStack>

      <VStack align="stretch" gap={3}>
        <Box>
          <Text fontSize="xs" color="fg.muted" fontWeight="semibold" textTransform="uppercase">
            Type
          </Text>
          <HStack gap={2} mt={1}>
            <Box w={3} h={3} borderRadius="full" bg={node.color} />
            <Text>{TYPE_LABELS[node.type] || node.type}</Text>
          </HStack>
        </Box>

        <Box>
          <Text fontSize="xs" color="fg.muted" fontWeight="semibold" textTransform="uppercase">
            Topic Path
          </Text>
          <Code mt={1} fontSize="sm" p={1}>
            {node.id}
          </Code>
        </Box>

        <Box>
          <Text fontSize="xs" color="fg.muted" fontWeight="semibold" textTransform="uppercase">
            Last Update
          </Text>
          <Text mt={1}>{timeSinceUpdate}</Text>
        </Box>

        {node.type === 'property' && node.value != null && (
          <Box>
            <Text fontSize="xs" color="fg.muted" fontWeight="semibold" textTransform="uppercase">
              Current Value
            </Text>
            <Box
              bg="bg.subtle"
              p={3}
              borderRadius="md"
              fontFamily="mono"
              fontSize="sm"
              mt={1}
              maxH="200px"
              overflow="auto"
            >
              {node.value}
            </Box>
          </Box>
        )}

        {childNodes.length > 0 && (
          <Box>
            <Text fontSize="xs" color="fg.muted" fontWeight="semibold" textTransform="uppercase">
              Children ({childNodes.length})
            </Text>
            <VStack align="stretch" gap={1} mt={1}>
              {childNodes.map((child) => (
                <HStack key={child.id} gap={2} py={1} px={2} borderRadius="md" bg="bg.subtle">
                  <Box w={2} h={2} borderRadius="full" bg={child.color} flexShrink={0} />
                  <Text fontSize="sm" flex={1} truncate>
                    {child.label}
                  </Text>
                  {child.type === 'property' && child.value != null && (
                    <Text fontSize="xs" color="fg.muted" fontFamily="mono" truncate maxW="100px">
                      {child.value}
                    </Text>
                  )}
                </HStack>
              ))}
            </VStack>
          </Box>
        )}
      </VStack>
    </Box>
  )
}
